"use client";

import { useState } from "react";
import { GripVertical, Plus, SlidersHorizontal, Trash2, Vote } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { PollAppearance, PollBlock, PollOption } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { useResolvedGridGap } from "../../hooks/useGridGap";
import {
    POLL_CONFIG,
    createPollOption,
    resolvePollAppearance,
} from "../../lib/blockRegistry";
import {
    AppearanceDivider,
    AppearanceReset,
    AppearanceRow,
    ColorChipField,
    ToolbarDivider,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";

export default function PollSettings({ block }: { block: PollBlock }) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const setBlockGridGap = useLomdaV3Editor((s) => s.setBlockGridGap);
    const gridGap = useResolvedGridGap(block.id);

    const { options } = block.data;
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const appearance = resolvePollAppearance(block.data.appearance, theme);
    const setOptions = (next: PollOption[]) =>
        replaceBlockData(block.id, { ...block.data, options: next });

    return (
        <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
                <span className="shrink-0 text-sm !font-semibold text-foreground">
                    {t("lomda-v3-editor.interactive.poll")}
                </span>
                <WidthControl blockId={block.id} />
                <div className="flex shrink-0 items-center gap-2">
                    <span className="whitespace-nowrap text-sm text-foreground">
                        {t("lomda-v3-editor.settings.grid-gap")}
                    </span>
                    <div className="flex h-9 w-16 items-center rounded-lg border px-2 text-sm text-foreground">
                        <input
                            type="number"
                            min={0}
                            value={gridGap}
                            onChange={(e) =>
                                setBlockGridGap(
                                    block.id,
                                    Math.max(0, parseInt(e.target.value, 10) || 0)
                                )
                            }
                            className="w-full bg-transparent text-center outline-none"
                        />
                    </div>
                </div>
            </div>

            <ToolbarDivider />

            <AppearancePopover
                appearance={appearance}
                onChange={(patch) =>
                    replaceBlockData(block.id, {
                        ...block.data,
                        appearance: { ...appearance, ...patch },
                    })
                }
                onReset={() =>
                    replaceBlockData(block.id, {
                        ...block.data,
                        appearance: undefined,
                    })
                }
            />
            <ManageOptionsPopover options={options} onChange={setOptions} />
        </div>
    );
}

function ManageOptionsPopover({
    options,
    onChange,
}: {
    options: PollOption[];
    onChange: (options: PollOption[]) => void;
}) {
    const LETTERS = ["A", "B", "C", "D", "E", "F"];
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const move = (from: number, to: number) => {
        if (from === to) return;
        const next = [...options];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        onChange(next);
    };
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <Vote className="size-5" />
                    {t("lomda-v3-editor.poll.manage-options")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[352px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.poll.manage-options")}
                    </PopoverTitle>
                    {options.map((o, i) => (
                        <div
                            key={o.id}
                            draggable
                            onDragStart={() => setDragIndex(i)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                                if (dragIndex !== null) move(dragIndex, i);
                                setDragIndex(null);
                            }}
                            className="flex items-center gap-2.5"
                        >
                            <GripVertical className="size-5 shrink-0 cursor-grab text-muted-foreground" />
                            <span className="text-sm text-primary">
                                {LETTERS[i] ?? i + 1}
                            </span>
                            <input
                                value={o.text.replace(/<[^>]*>/g, "")}
                                onChange={(e) =>
                                    onChange(
                                        options.map((x) =>
                                            x.id === o.id
                                                ? { ...x, text: e.target.value }
                                                : x
                                        )
                                    )
                                }
                                placeholder={t(
                                    "lomda-v3-editor.poll.option-placeholder"
                                )}
                                className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={options.length <= 2}
                                onClick={() =>
                                    onChange(options.filter((x) => x.id !== o.id))
                                }
                                className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
                            >
                                <Trash2 className="size-4" />
                            </Button>
                        </div>
                    ))}
                    <span className="h-px w-full bg-muted" />
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onChange([...options, createPollOption()])}
                        className="h-10 gap-2 self-start rounded-lg bg-transparent px-3 text-sm hover:bg-accent"
                    >
                        <Plus className="size-5" />
                        {t("lomda-v3-editor.poll.add")}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

function AppearancePopover({
    appearance,
    onChange,
    onReset,
}: {
    appearance: PollAppearance;
    onChange: (patch: Partial<PollAppearance>) => void;
    onReset: () => void;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <SlidersHorizontal className="size-5" />
                    {t("lomda-v3-editor.poll.appearance")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[320px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.poll.appearance")}
                    </PopoverTitle>
                    {POLL_CONFIG.appearanceGroups.map((group, gi) => (
                        <div key={gi} className="flex flex-col gap-4">
                            {gi > 0 && <AppearanceDivider />}
                            {group.map((field) => {
                                const apKey = field.key as keyof PollAppearance;
                                const value = appearance[apKey];
                                return (
                                    <AppearanceRow
                                        key={field.key}
                                        label={t(field.labelKey)}
                                    >
                                        {field.control === "borderWidth" ? (
                                            <div className="flex h-9 w-14 items-center justify-center rounded-lg border border-border bg-card px-2 text-sm text-foreground">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={8}
                                                    value={Number(value)}
                                                    onChange={(e) =>
                                                        onChange({
                                                            [apKey]: Math.max(
                                                                0,
                                                                parseInt(
                                                                    e.target
                                                                        .value,
                                                                    10
                                                                ) || 0
                                                            ),
                                                        })
                                                    }
                                                    className="w-6 bg-transparent text-center outline-none"
                                                />
                                                <span className="text-muted-foreground">
                                                    px
                                                </span>
                                            </div>
                                        ) : (
                                            <ColorChipField
                                                value={String(value)}
                                                onChange={(c) =>
                                                    onChange({ [apKey]: c })
                                                }
                                            />
                                        )}
                                    </AppearanceRow>
                                );
                            })}
                        </div>
                    ))}
                    <AppearanceReset onReset={onReset} />
                </div>
            </PopoverContent>
        </Popover>
    );
}
